import React from "react";
import { motion } from "framer-motion";
import Section from "./Section";
import {
  PhoneGuestMockup,
  PhoneStaffMockup,
  LaptopDashboardMockup,
} from "./DeviceMockups";
import { ArrowUpRight } from "lucide-react";

const products = [
  {
    id: "guest",
    kicker: "Guest",
    title: "GUEST",
    logo: "/brand/guest-logo.png",
    tagline: "Your hotel, in every guest's pocket.",
    body: "A multilingual mobile experience for pre check-in, digital keys, room service, requests, offers, dining and taxi bookings — designed to feel like a five-star concierge.",
    features: [
      "Multilingual communication",
      "Digital pre check-in & room key",
      "Room service & requests",
      "Offers · dining · cab booking",
    ],
    Mockup: PhoneGuestMockup,
  },
  {
    id: "staff",
    kicker: "Staff",
    title: "STAFF",
    logo: "/brand/staff-logo.png",
    tagline: "One app for every department.",
    body: "A mobile-first staff app: task management, escalations, and cross-department collaboration for housekeeping, engineering, F&B and front desk. Nothing gets missed — SLAs stay green.",
    features: [
      "Task management & notifications",
      "Request tracking · escalations",
      "Housekeeping & maintenance",
      "Department collaboration",
    ],
    Mockup: PhoneStaffMockup,
  },
  {
    id: "hop",
    kicker: "HOP",
    title: "HOP Dashboard",
    logo: "/brand/hop-logo.png",
    tagline: "The operating system for hotel leadership.",
    body: "A cloud-native command centre for GMs and owners. Real-time operations, SLA tracking, performance analytics, and PMS/POS integrations in a single pane of glass.",
    features: [
      "Operations dashboard & reports",
      "Analytics · SLA · performance",
      "PMS & POS integrations",
      "Management insights",
    ],
    Mockup: LaptopDashboardMockup,
  },
];

export default function PlatformSection({ showHeader = true }) {
  return (
    <Section
      id="platform"
      overline={showHeader ? "The platform" : undefined}
      testId="platform-section"
    >
      {showHeader && (
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
          <h2 className="font-display text-white text-[36px] md:text-[56px] leading-[1.02] tracking-tight max-w-3xl" style={{ fontWeight: 800 }}>
            Three products.{" "}
            <span className="grad-text">One connected</span>{" "}
            hospitality platform.
          </h2>
          <p className="text-white/70 text-[16px] max-w-md">
            Guests, staff, and management — finally speaking the same language,
            in real time, across every property you run.
          </p>
        </div>
      )}

      <div className={`${showHeader ? "mt-16" : ""} space-y-24 md:space-y-32`}>
        {products.map((p, i) => {
          const reverse = i % 2 === 1;
          return (
            <div
              key={p.id}
              id={p.id}
              className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center"
              data-testid={`product-${p.id}`}
            >
              <motion.div
                initial={{ opacity: 0, x: reverse ? 40 : -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className={`md:col-span-5 ${reverse ? "md:order-2" : ""}`}
              >
                <div className="flex items-center gap-4">
                  <img
                    src={p.logo}
                    alt={`${p.kicker} by iNPLASS`}
                    className="w-16 h-16 rounded-full bg-white p-1.5 shadow-lg"
                    data-testid={`product-logo-${p.id}`}
                  />
                  <div>
                    <div className="font-display text-[11px] uppercase tracking-[0.28em] text-brand-orange font-bold">
                      {p.kicker}
                    </div>
                    <h3 className="font-display text-white text-[30px] md:text-[40px] leading-[1.05]" style={{ fontWeight: 800 }}>
                      {p.title}
                    </h3>
                  </div>
                </div>
                <div className="mt-4 grad-text font-display text-[19px] font-semibold italic">
                  {p.tagline}
                </div>
                <p className="mt-5 text-white/75 leading-relaxed">{p.body}</p>
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {p.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2 text-white/80 text-[14px]"
                    >
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-brand-orange" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href={`#${p.id}`}
                  className="mt-8 inline-flex items-center gap-2 text-white group text-[14px]"
                  data-testid={`product-${p.id}-cta`}
                >
                  <span className="border-b border-white/40 group-hover:border-white pb-0.5">
                    Explore {p.kicker}
                  </span>
                  <ArrowUpRight
                    size={16}
                    className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{
                  duration: 0.9,
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.1,
                }}
                className={`md:col-span-7 flex items-center justify-center ${
                  reverse ? "md:order-1" : ""
                }`}
              >
                <p.Mockup />
              </motion.div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}
